import React from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => (
  <div className="heritage-canvas">
    <SEO
      title="Privacy Policy"
      description="How AfriMarket collects, uses and protects your personal data — UK GDPR compliant."
      path="/privacy"
    />
    <div className="container-narrow py-16 max-w-3xl">
      <div className="label-overline text-[#C05A3C] mb-3">Legal</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-3">Privacy Policy</h1>
      <p className="text-sm text-muted-foreground mb-10">
        Last updated: 18 February 2026 · UK GDPR compliant
      </p>

      <div className="prose-legal space-y-7 text-[15px] leading-relaxed text-[#1A1A1A]">
        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">1. Who we are</h2>
          <p>
            <strong>AfriMarket Group Ltd</strong> (company number <strong>17282940</strong>, registered in
            England &amp; Wales) is the data controller for personal data collected through the AfriMarket
            platform. You can contact us at{" "}
            <a href="mailto:admin@afrimarket.org" className="text-[#C05A3C] underline">admin@afrimarket.org</a>.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">2. What data we collect</h2>
          <p>We only collect data necessary to operate and improve the Platform:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Account data</strong> — name, email address, password (hashed), country, optional phone number.</li>
            <li><strong>Seller verification data</strong> — proof of ID and address (encrypted at rest, accessed only by admins for KYC review).</li>
            <li><strong>Listing data</strong> — titles, descriptions, prices, photos and category metadata you upload.</li>
            <li><strong>Transaction data</strong> — orders, payment status, dispute history. Card details are handled by Stripe / PayPal and never stored by us.</li>
            <li><strong>Communication data</strong> — messages exchanged with other users on the Platform.</li>
            <li><strong>Location data</strong> — country and city you set in your profile, plus (only if you click &quot;Use my location&quot;) your approximate browser geolocation to surface nearby listings.</li>
            <li><strong>Device &amp; usage data</strong> — IP address, browser type, pages viewed, referrer URL, cookie identifiers.</li>
            <li><strong>Email-preference data</strong> — whether you subscribe to our weekly digest.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">3. Why we process your data (lawful bases)</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>To provide the Platform</strong> — performance of our contract with you (Art. 6(1)(b) UK GDPR).</li>
            <li><strong>For seller KYC and trust &amp; safety</strong> — legitimate interest in preventing fraud and protecting buyers.</li>
            <li><strong>For payment processing</strong> — performance of contract; we share necessary fields with Stripe and PayPal.</li>
            <li><strong>For marketing emails &amp; cookies</strong> — your consent, which you can withdraw at any time.</li>
            <li><strong>For legal compliance</strong> — to meet our obligations under UK tax, anti-money-laundering and consumer-protection law.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">4. Who we share data with</h2>
          <p>We share data only with the third-party processors necessary to run the Platform:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Stripe, Inc.</strong> &amp; <strong>PayPal Holdings Inc.</strong> — payment processing (US, with UK/EEA data-transfer safeguards).</li>
            <li><strong>Resend.com</strong> — transactional and digest emails (US, with SCCs in place).</li>
            <li><strong>MongoDB Atlas</strong> — encrypted database hosting (EU region).</li>
            <li><strong>Cloudflare</strong> — DDoS protection and CDN.</li>
            <li><strong>OpenAI / Google / Anthropic</strong> — when you use AI search or AI-generated descriptions, the relevant prompt is sent to the LLM provider. We do not include personal contact details in prompts.</li>
            <li><strong>UK / international tax authorities</strong> — only where legally required.</li>
          </ul>
          <p>We do <strong>not</strong> sell your personal data to advertisers.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">5. International transfers</h2>
          <p>
            Some of our processors are based outside the UK/EEA. For each transfer we rely on appropriate
            safeguards — typically the UK International Data Transfer Agreement or the EU Standard Contractual
            Clauses with the UK Addendum.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">6. How long we keep your data</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Active account data — until you close your account.</li>
            <li>Closed account data — up to 6 years (UK tax law) for transactions; 30 days for non-financial data.</li>
            <li>Verification documents — deleted within 90 days of your account closure unless required for ongoing investigations.</li>
            <li>Server logs — 30 days.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">7. Your rights under UK GDPR</h2>
          <p>You have the right to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Access</strong> a copy of the data we hold about you.</li>
            <li><strong>Rectify</strong> inaccurate or incomplete data.</li>
            <li><strong>Erase</strong> your data (&quot;right to be forgotten&quot;), subject to legal retention requirements.</li>
            <li><strong>Restrict</strong> or <strong>object</strong> to certain processing.</li>
            <li><strong>Data portability</strong> — receive your data in a machine-readable format.</li>
            <li><strong>Withdraw consent</strong> for marketing emails or non-essential cookies at any time.</li>
            <li><strong>Complain</strong> to the UK Information Commissioner&apos;s Office (<a href="https://ico.org.uk" className="text-[#C05A3C] underline">ico.org.uk</a>) if you believe we have mishandled your data.</li>
          </ul>
          <p>
            To exercise any of these rights email{" "}
            <a href="mailto:admin@afrimarket.org" className="text-[#C05A3C] underline">admin@afrimarket.org</a>.
            We respond within 30 days.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">8. Cookies</h2>
          <p>
            See our <Link to="/cookies" className="text-[#C05A3C] underline">Cookie Policy</Link> for the full
            list of cookies we use and how to control them.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">9. Children</h2>
          <p>
            AfriMarket is not directed at children under 18. If you believe a child has provided us with
            personal data, please contact us and we will delete it.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl mt-8 mb-3">10. Changes</h2>
          <p>
            We may update this Privacy Policy. The &quot;Last updated&quot; date reflects the latest version.
            Material changes are notified by email.
          </p>
          <p className="text-sm text-muted-foreground mt-6">
            AfriMarket Group Ltd · Company No. 17282940 · Registered in England &amp; Wales.
          </p>
        </section>
      </div>
    </div>
  </div>
);

export default PrivacyPolicy;
